import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Loader from 'components/D3Loader'
import { browserHistory } from 'utils/navigation'
import { modalActions } from 'store/modal'
import {
  MODAL_BUTTONS_MAP,
  MODAL_CLASSES_MAP,
} from 'utils/constants'
import {
  getStudyVersionID,
  hasMultiScheduleFeature,
  isVersionUatOrLive,
} from '../../../../../../../../../../../utils/studyConfigUtils'
import { buildInstrumentNameMap } from '../../InstrumentPreview/utils/schedulePreviewHelpers'
import { getInvalidVisitTriggeredScheduleIndexes } from 'utils/chainDeploymentUtils'
import STRINGS from 'utils/strings'
import { instrumentActions, schedulerActions } from '../../../modules/Instrument'
import AllSchedulesPage from '../components/AllSchedulesPage'

const AllSchedulesContainer = props => {
  const {
    loading,
    instrument,
    confirmedSchedules,
    studyID,
    studyVersionID,
    isReadOnlyState,
    hasMultiSchedule,
    startNewSchedule,
    loadScheduleForEdit,
    deleteConfirmedSchedule,
    deployConfirmedSchedules,
    openModal,
    closeModal,
    participants,
    sites,
    instrumentList,
    visitTemplates,
    studyVisitSchedule,
    isVisitCycleSupported,
    isArmsSupported,
  } = props

  const [isDeployDisabled, setIsDeployDisabled] = useState(false)

  const nameContext = useMemo(
    () => ({
      instrumentNameMap: buildInstrumentNameMap(instrumentList),
      visitTemplates,
      studyVisitSchedule,
      isVisitCycleSupported,
      isArmsSupported,
    }),
    [instrumentList, visitTemplates, studyVisitSchedule, isVisitCycleSupported, isArmsSupported],
  )

  const invalidVisitScheduleIndexes = useMemo(
    () =>
      getInvalidVisitTriggeredScheduleIndexes(confirmedSchedules, {
        visitTemplates,
        studyVisitSchedule,
        isVisitCycleSupported,
      }),
    [confirmedSchedules, visitTemplates, studyVisitSchedule, isVisitCycleSupported],
  )
  const hasInvalidVisitSchedules = invalidVisitScheduleIndexes.length > 0

  if (loading || !instrument) {
    return Loader(props)
  }

  const cohortMap = participants?.cohortMap || {}

  const goToScheduleEditor = () => {
    browserHistory.push({
      pathname: `/studies/${studyID}/${studyVersionID}/instruments/${instrument.id}/schedule`,
      state: { keepTriggerDraft: true },
    })
  }

  const onNewSchedule = () => {
    if (!hasMultiSchedule) return
    startNewSchedule()
    goToScheduleEditor()
  }

  const onEditSchedule = (_schedule, index) => {
    loadScheduleForEdit(index)
    goToScheduleEditor()
  }

  const onDeleteSchedule = (_schedule, index) => {
    if (!hasMultiSchedule) return
    const scheduleName = `Schedule ${index + 1}`
    openModal({
      content: `Are you sure you want to delete ${scheduleName}? This cannot be undone.`,
      className: MODAL_CLASSES_MAP.confirmation,
      confirmButton: MODAL_BUTTONS_MAP.confirm,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      onConfirm: () => {
        Promise.resolve(deleteConfirmedSchedule(index))
          .then(() => closeModal())
          .catch(() => closeModal())
      },
    })
  }

  const onScheduleInstrument = () => {
    setIsDeployDisabled(true)
    deployConfirmedSchedules({
      onFail: () => setIsDeployDisabled(false),
    })
  }

  return (
    <AllSchedulesPage
      instrument={instrument}
      confirmedSchedules={confirmedSchedules}
      cohortMap={cohortMap}
      siteSelectionList={sites?.siteSelectionList}
      nameContext={nameContext}
      studyID={studyID}
      studyVersionID={studyVersionID}
      isReadOnlyState={isReadOnlyState}
      hasMultiSchedule={hasMultiSchedule}
      invalidVisitScheduleIndexes={invalidVisitScheduleIndexes}
      visitScheduleAlertMessage={
        hasInvalidVisitSchedules ? STRINGS.errSomeSchedulesTriggeredByVisitNeedEdit : ''
      }
      onNewSchedule={onNewSchedule}
      onEditSchedule={onEditSchedule}
      onDeleteSchedule={onDeleteSchedule}
      onScheduleInstrument={onScheduleInstrument}
      isDeployDisabled={isDeployDisabled}
    />
  )
}

AllSchedulesContainer.propTypes = {
  loading: PropTypes.bool,
  instrument: PropTypes.object,
  confirmedSchedules: PropTypes.array,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnlyState: PropTypes.bool,
  hasMultiSchedule: PropTypes.bool,
  startNewSchedule: PropTypes.func,
  loadScheduleForEdit: PropTypes.func,
  deleteConfirmedSchedule: PropTypes.func,
  deployConfirmedSchedules: PropTypes.func,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  participants: PropTypes.object,
  sites: PropTypes.object,
  instrumentList: PropTypes.array,
  visitTemplates: PropTypes.array,
  studyVisitSchedule: PropTypes.array,
  isVisitCycleSupported: PropTypes.bool,
  isArmsSupported: PropTypes.bool,
}

const mapState = state => {
  const {
    instrumentReducer,
    loading,
    study,
    location,
    participants,
    sites,
    instruments,
    participantReducer,
    visitSchedules,
  } = state
  const studyVersionID = getStudyVersionID(location)
  const { currentStudy, studyVersionList } = study
  const hasStudyPromotion = !!currentStudy?.config?.study_promotion
  const isReadOnlyState = hasStudyPromotion && isVersionUatOrLive(studyVersionList, studyVersionID)
  const isVisitCycleSupported = currentStudy?.config?.visit_support?.version > 1
  const isArmsSupported = !!currentStudy?.config?.visit_support?.arms_enabled
  const hasMultiSchedule = hasMultiScheduleFeature(currentStudy)

  return {
    ...instrumentReducer,
    loading: loading.general,
    studyID: currentStudy.id,
    studyVersionID,
    isReadOnlyState,
    hasMultiSchedule,
    participants,
    sites,
    instrumentList: instruments?.instrumentList || instruments?.allInstruments || [],
    visitTemplates: participantReducer?.visitTemplates || [],
    studyVisitSchedule: visitSchedules?.studyVisitSchedule || [],
    isVisitCycleSupported,
    isArmsSupported,
  }
}

const mapDispatchToProps = {
  ...instrumentActions,
  ...schedulerActions,
  ...modalActions,
}

export default connect(
  mapState,
  mapDispatchToProps,
)(AllSchedulesContainer)
